import { useState } from "react";
import { AlertCircle, Check, Loader2, Upload } from "lucide-react";
import { CodeBlock, JsonBlock } from "@/components/CodeBlock";
import { Field, FieldGroup } from "@/components/HashDisplay";
import { Prop } from "@/components/Prop";
import { toHex } from "@/hashtree/bytes";
import { KNOWN_SERVERS, blobUrl, buildUploadAuth, uploadBlob } from "@/hashtree/blossom";
import type { HashtreeBuild, UploadRow } from "@/hashtree/useHashtreeBuild";
import { cn } from "@/lib/utils";
import { Slide, SpecNote } from "./Slide";

/** A tiny chunk size can produce thousands of blobs; only list the first page. */
const ROW_LIMIT = 100;

export function UploadSlide({ build, onNext }: { build: HashtreeBuild; onNext: () => void }) {
  const { tree, server, setServer, identity, uploads, setUploads } = build;
  const [running, setRunning] = useState(false);

  if (!tree) return null;

  const rows: UploadRow[] =
    uploads.length === tree.blobs.length
      ? uploads
      : tree.blobs.map((node) => ({ node, state: "pending" as const }));

  const done = rows.filter((row) => row.state === "done").length;
  const failed = rows.filter((row) => row.state === "error").length;
  const allDone = done === rows.length && rows.length > 0;

  const run = async () => {
    setRunning(true);
    // Children before parents, so a parent is never resolvable before the
    // blobs it points at exist.
    let current = tree.blobs.map((node) => ({ node, state: "pending" as const }) as UploadRow);
    setUploads(current);

    for (let i = 0; i < current.length; i++) {
      current = current.map((row, index) => (index === i ? { ...row, state: "uploading" } : row));
      setUploads(current);

      const { node } = current[i]!;
      try {
        const result = await uploadBlob(
          server,
          node.encrypted.ciphertext,
          node.encrypted.blobHash,
          identity.secretKey,
        );
        current = current.map((row, index) =>
          index === i ? { ...row, state: "done", hashMatches: result.hashMatches } : row,
        );
      } catch (error) {
        current = current.map((row, index) =>
          index === i
            ? { ...row, state: "error", message: error instanceof Error ? error.message : String(error) }
            : row,
        );
      }
      setUploads(current);
    }
    setRunning(false);
  };

  const sampleAuth = buildUploadAuth(toHex(tree.blobs[0]!.encrypted.blobHash));

  return (
    <Slide
      title="Upload the blobs"
      lede={
        <>
          Now the part that needs no new BUD at all. Chunks and manifests alike are just blobs, uploaded with the same
          BUD-02 endpoint any Blossom client uses. The server never learns that these bytes form a tree.
        </>
      }
    >
      <div className="space-y-1.5">
        <label htmlFor="server" className="text-sm font-medium">
          Blossom server
        </label>
        <div className="flex flex-wrap gap-1.5">
          {KNOWN_SERVERS.map((candidate) => (
            <button
              key={candidate}
              type="button"
              onClick={() => setServer(candidate)}
              className={cn(
                "rounded-md border px-2 py-1 font-mono text-xs transition-colors",
                server === candidate ? "border-primary bg-primary/10 font-medium" : "hover:bg-muted",
              )}
            >
              {candidate.replace("https://", "")}
            </button>
          ))}
        </div>
        <input
          id="server"
          value={server}
          onChange={(event) => setServer(event.target.value)}
          className="w-full max-w-md rounded-md border bg-background px-3 py-1.5 font-mono text-sm"
        />
        <p className="text-xs text-muted-foreground">
          Not every Blossom server accepts these. Some only allow recognised media types, and a hashtree uploads
          ciphertext and MessagePack — the servers listed above were checked against this app.
        </p>
      </div>

      <SpecNote tone="warn">
        Only ciphertext and its hash go over the wire. No <Prop name="k" /> value is ever sent to a server — the whole
        design depends on that.
      </SpecNote>

      <details className="rounded-lg border bg-card">
        <summary className="cursor-pointer px-4 py-2.5 text-sm font-medium">
          The kind 24242 authorization event sent with each upload
        </summary>
        <div className="space-y-2 border-t px-4 py-3">
          <JsonBlock value={sampleAuth} caption="unsigned template for the first blob" />
          <p className="text-xs text-muted-foreground">
            The <Prop name="x" /> tag commits the token to one specific blob hash, so a server can verify the bytes it
            received are the ones you authorized. It is signed with the throwaway key generated for this walkthrough.
          </p>
          <FieldGroup>
            <Field label="npub" value={identity.npub} hint="A fresh key made in your browser for this tree" />
          </FieldGroup>
        </div>
      </details>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => void run()}
          disabled={running}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {running ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />}
          {allDone ? "Upload again" : `Upload ${rows.length} blobs`}
        </button>
        <span className="text-xs text-muted-foreground">
          {done}/{rows.length} uploaded{failed > 0 && `, ${failed} failed`}
        </span>
      </div>

      <div className="overflow-hidden rounded-lg border">
        {rows.slice(0, ROW_LIMIT).map((row) => (
          <div key={row.node.id} className="grid grid-cols-[1.5rem_1fr] items-start gap-2 border-b px-3 py-2 last:border-b-0">
            <div className="pt-0.5">
              {row.state === "done" && <Check className="size-4 text-emerald-500" />}
              {row.state === "uploading" && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
              {row.state === "error" && <AlertCircle className="size-4 text-red-500" />}
              {row.state === "pending" && <div className="size-2 rounded-full bg-muted-foreground/30" />}
            </div>
            <div className="min-w-0 space-y-0.5">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="text-xs font-medium">{row.node.label}</span>
                <span className="text-[0.65rem] text-muted-foreground">
                  <Prop name="t" value={row.node.kind === "blob" ? 0 : row.node.nodeType!} />
                </span>
                <span className="text-[0.65rem] text-muted-foreground">
                  {row.node.encrypted.ciphertext.length} B ciphertext
                </span>
                {row.state === "done" && row.hashMatches && (
                  <span className="text-[0.65rem] text-emerald-600 dark:text-emerald-400">server hash matches</span>
                )}
              </div>
              <code className="block font-mono text-xs break-all text-sky-700 select-all dark:text-sky-300">
                {toHex(row.node.encrypted.blobHash)}
              </code>
              {row.state === "error" && (
                <p className="text-xs text-red-600 dark:text-red-400">
                  {row.message} — try a different server above.
                </p>
              )}
              {row.state === "done" && (
                <a
                  href={blobUrl(server, toHex(row.node.encrypted.blobHash))}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[0.65rem] text-muted-foreground underline underline-offset-2 hover:text-foreground"
                >
                  open blob
                </a>
              )}
            </div>
          </div>
        ))}
        {rows.length > ROW_LIMIT && (
          <div className="px-3 py-2 text-xs text-muted-foreground">
            and {rows.length - ROW_LIMIT} more blobs — all of them upload, only the first {ROW_LIMIT} are listed here.
          </div>
        )}
      </div>

      <CodeBlock
        caption="BUD-02 upload"
        code={`PUT /upload
Authorization: Nostr <base64 kind 24242 event>
Content-Type: application/octet-stream

<ciphertext bytes>`}
      />

      <button
        type="button"
        onClick={onNext}
        disabled={!allDone}
        className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
      >
        {allDone ? "Publish the root" : "Upload every blob to continue"}
      </button>
    </Slide>
  );
}
